import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import jwt_decode from 'jwt-decode';
import API from './utils/api';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// Pages
import Home from './pages/Home';
import Services from './pages/Services';
import About from './pages/About';
import Contact from './pages/Contact';
import Login from './pages/Login';
import Register from './pages/Register';
import NotFound from './pages/NotFound';

// Owner Pages
import OwnerDashboard from './pages/owner/Dashboard';

import OwnerManagement from './pages/owner/Management';
import OwnerAccountManagement from './pages/owner/AccountManagement';
import OwnerStructure from './pages/owner/Structure';

// Admin Pages
import AdminDashboard from './pages/admin/Dashboard';
import AdminBookings from './pages/admin/Bookings';
import AdminUsers from './pages/admin/Users';
import AdminMechanics from './pages/admin/Mechanics';
import AdminServices from './pages/admin/Services';
import AdminServiceDetail from './pages/admin/ServiceDetail';

import BookingSchedule from './pages/admin/BookingSchedule';
import AdminTransactions from './pages/admin/Transactions';

// Staff Pages
import StaffDashboard from './pages/staff/Dashboard';
import StaffPembayaran from './pages/staff/Pembayaran';
import StaffRiwayatTransaksi from './pages/staff/RiwayatTransaksi';

// Mechanic Pages
import MechanicDashboard from './pages/mechanic/Dashboard';
import MechanicJobs from './pages/mechanic/Jobs';
import MechanicSchedule from './pages/mechanic/Schedule';
import MechanicFieldNotes from './pages/mechanic/FieldNotes';

// Customer Pages
import CustomerDashboard from './pages/customer/Dashboard';
import CustomerBookings from './pages/customer/Bookings';
import CustomerBook from './pages/customer/Book';
import CustomerServices from './pages/customer/Services';

// Components
import Navbar from './components/Navbar';

function App() {
  const [auth, setAuth] = useState({
    isAuthenticated: false,
    user: null,
    loading: true
  });

  useEffect(() => {
    // Check if user is already logged in
    const checkAuth = async () => {
      const token = localStorage.getItem('token');

      if (token) {
        try {
          // Add token to all requests handled by API instance di utils/api.js
          // axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

          // Decode token to get user info
          const decoded = jwt_decode(token);

          // Check if token is expired
          const currentTime = Date.now() / 1000;
          if (decoded.exp < currentTime) {
            // Token expired
            localStorage.removeItem('token');
            // delete axios.defaults.headers.common['Authorization'];
            setAuth({ isAuthenticated: false, user: null, loading: false });
            return;
          }

          // Verify token with backend
          const response = await API.get('/api/auth/verify-token');
          setAuth({
            isAuthenticated: true,
            user: response.data.user,
            loading: false
          });
        } catch (error) {
          console.error('Auth error:', error);
          localStorage.removeItem('token');
          // delete axios.defaults.headers.common['Authorization'];
          setAuth({ isAuthenticated: false, user: null, loading: false });
        }
      } else {
        setAuth({ isAuthenticated: false, user: null, loading: false });
      }
    };

    checkAuth();
  }, []);

  const logout = () => {
    localStorage.removeItem('token');
    // delete axios.defaults.headers.common['Authorization'];
    setAuth({ isAuthenticated: false, user: null, loading: false });
  };

  // Protected route component
  const ProtectedRoute = ({ children, allowedRoles }) => {
    if (auth.loading) {
      return <div>Loading...</div>;
    }

    if (!auth.isAuthenticated) {
      return <Navigate to="/login" />;
    }

    if (allowedRoles && !allowedRoles.includes(auth.user.role)) {
      return <Navigate to="/" />;
    }

    return children;
  };

  return (
    <Router>
      <ToastContainer position="top-right" autoClose={3000} hideProgressBar={false} newestOnTop closeOnClick rtl={false} pauseOnFocusLoss draggable pauseOnHover />
      <div className="App">
        <Navbar
          isAuthenticated={auth.isAuthenticated}
          userRole={auth.user?.role}
          logout={logout}
        />

        <Routes>
          {/* Public Routes */}
          <Route path="/" element={<Home />} />
          <Route path="/services" element={<Services />} />
          <Route path="/about" element={<About />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/login" element={
            auth.isAuthenticated ? <Navigate to="/" /> : <Login setAuth={setAuth} />
          } />
          <Route path="/register" element={
            auth.isAuthenticated ? <Navigate to="/" /> : <Register setAuth={setAuth} />
          } />

          {/* Owner Routes */}
          <Route path="/owner/dashboard" element={
            <ProtectedRoute allowedRoles={['owner']}>
              <OwnerDashboard />
            </ProtectedRoute>
          } />
          <Route path="/owner/reports" element={
            <Navigate to="/owner/dashboard" />
          } />
          <Route path="/owner/management" element={
            <ProtectedRoute allowedRoles={['owner']}>
              <OwnerManagement />
            </ProtectedRoute>
          } />
          <Route path="/owner/structure" element={
            <ProtectedRoute allowedRoles={['owner']}>
              <OwnerStructure />
            </ProtectedRoute>
          } />
          <Route path="/owner/account-management" element={
            <ProtectedRoute allowedRoles={['owner']}>
              <OwnerAccountManagement />
            </ProtectedRoute>
          } />

          {/* Admin Routes */}
          <Route path="/admin/dashboard" element={
            <ProtectedRoute allowedRoles={['admin']}>
              <AdminDashboard />
            </ProtectedRoute>
          } />
          <Route path="/admin/bookings" element={
            <ProtectedRoute allowedRoles={['admin']}>
              <AdminBookings />
            </ProtectedRoute>
          } />
          <Route path="/admin/booking-schedule" element={
            <ProtectedRoute allowedRoles={['admin']}>
              <BookingSchedule />
            </ProtectedRoute>
          } />
          <Route path="/admin/users" element={
            <ProtectedRoute allowedRoles={['admin']}>
              <AdminUsers />
            </ProtectedRoute>
          } />
          <Route path="/admin/mechanics" element={
            <ProtectedRoute allowedRoles={['admin']}>
              <AdminMechanics />
            </ProtectedRoute>
          } />
          <Route path="/admin/services" element={
            <ProtectedRoute allowedRoles={['admin']}>
              <AdminServices />
            </ProtectedRoute>
          } />
          <Route path="/admin/services/:id" element={
            <ProtectedRoute allowedRoles={['admin']}>
              <AdminServiceDetail />
            </ProtectedRoute>
          } />
          <Route path="/admin/transactions" element={
            <ProtectedRoute allowedRoles={['admin']}>
              <AdminTransactions />
            </ProtectedRoute>
          } />
          <Route path="/admin/reports" element={
            <Navigate to="/staff/dashboard" />
          } />

          {/* Staff Routes */}
          <Route path="/staff/dashboard" element={
            <ProtectedRoute allowedRoles={['staff']}>
              <StaffDashboard />
            </ProtectedRoute>
          } />
          <Route path="/staff/pembayaran" element={
            <ProtectedRoute allowedRoles={['staff']}>
              <StaffPembayaran />
            </ProtectedRoute>
          } />
          <Route path="/staff/riwayat-transaksi" element={
            <ProtectedRoute allowedRoles={['staff']}>
              <StaffRiwayatTransaksi />
            </ProtectedRoute>
          } />

          {/* Mechanic Routes */}
          <Route path="/mechanic/dashboard" element={
            <ProtectedRoute allowedRoles={['mechanic']}>
              <MechanicDashboard />
            </ProtectedRoute>
          } />
          <Route path="/mechanic/jobs" element={
            <ProtectedRoute allowedRoles={['mechanic']}>
              <MechanicJobs />
            </ProtectedRoute>
          } />
          <Route path="/mechanic/schedule" element={
            <ProtectedRoute allowedRoles={['mechanic']}>
              <MechanicSchedule />
            </ProtectedRoute>
          } />
          <Route path="/mechanic/field-notes" element={
            <ProtectedRoute allowedRoles={['mechanic']}>
              <MechanicFieldNotes />
            </ProtectedRoute>
          } />

          {/* Customer Routes */}
          <Route path="/customer/dashboard" element={
            <ProtectedRoute allowedRoles={['customer']}>
              <CustomerDashboard />
            </ProtectedRoute>
          } />
          <Route path="/customer/bookings" element={
            <ProtectedRoute allowedRoles={['customer']}>
              <CustomerBookings />
            </ProtectedRoute>
          } />
          <Route path="/customer/book" element={
            <ProtectedRoute allowedRoles={['customer']}>
              <CustomerBook />
            </ProtectedRoute>
          } />
          <Route path="/customer/services" element={
            <ProtectedRoute allowedRoles={['customer']}>
              <CustomerServices />
            </ProtectedRoute>
          } />

          {/* 404 Route */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </div>
    </Router>
  );
}

export default App;
